<script setup lang="ts">
// MDS UI components import
import "@maersk-global/mds-components-core-tab";
import "@maersk-global/mds-components-core-icon";
import "@maersk-global/mds-components-core-table";
import "@maersk-global/mds-components-core-select";
import "@maersk-global/mds-components-core-option";
import "@maersk-global/mds-components-core-tab-bar";
import "@maersk-global/mds-components-core-pagination";
// Vue imports
import { computed, ref } from "vue";
import { dateFormatWithoutOffset } from "@/utils/date_formatter";
import ContainerPrintView from "@/components/common/ContainerPrintView.vue";
import { useEquipmentHistoryStore } from "@/store/history/equipment_history_store";
import ShimmerLoaderTableView from "@/components/common/ShimmerLoaderTableView.vue";
import type { ValidIdDetails } from "@/interface/history/equipment_history_interface";
import { useTerminalConfigurationStore } from "@/store/common/terminal_configuration_store";
import { useEquipmentLocationStore } from "@/store/history/equipment_location_store";

import { useGlobalStore } from "@/store/global";

const locationStore = useEquipmentLocationStore();
const globalStore = useGlobalStore();
// Use the store
const equipmentHistoryStore = useEquipmentHistoryStore();
// Terminal configuration from store
const terminalConfigurationStore = useTerminalConfigurationStore();
// Reactive getters
const validIdDetail = computed((): ValidIdDetails[] => equipmentHistoryStore.validIdsDetail);
const loader = computed((): boolean => equipmentHistoryStore.isLoading);
const errorCode = computed((): string => equipmentHistoryStore.error || "");

const currentPage = ref<number>(1);

// Labels
const labels = {
  equipmentResult: globalStore.sitecoreData.appointment_types.container_event_history,
  equipmentLoader: globalStore.sitecoreData.common.equipment_loader,
  timeline: globalStore.sitecoreData.headings.container_time_line,
  noResult: globalStore.sitecoreData.search.not_found,
  operator: globalStore.sitecoreData.table_columns.equipment.operator,
  vesselDisclaimer: globalStore.sitecoreData.container_validation.schedule_disclaimer
};

const getFieldId = (fieldName: string): string => {
  switch (fieldName) {
    case "eventType":
      return "event_type";
    case "performedDateTimeLocal":
      return "performed_date_time_local";
    case "positionNotes":
      return "position_notes";
    default:
      return "";
  }
};

const getTerminalSpecificLabel = (fieldName: string): string => {
  const selectedOneStreamCode = locationStore.terminalSelected.oneStreamCode;
  if (globalStore.sitecoreData.terminal_specific[selectedOneStreamCode]?.table_columns?.equipment) {
    return (
      globalStore.sitecoreData.terminal_specific[selectedOneStreamCode]?.table_columns?.equipment[fieldName] ||
      globalStore.sitecoreData.table_columns.equipment[fieldName]
    );
  }
  return globalStore.sitecoreData.table_columns.equipment[fieldName];
};

// Container column name
const containerColumnNames = computed((): any => {
  const columnName = terminalConfigurationStore.equipmentConfiguration.containerColumnNames
    .filter((item: any): any => item.enabled) // Filter items where enabled is true
    .map((item: any) => {
      const fieldName = getFieldId(item.id);
      return {
        ...item,
        label: getTerminalSpecificLabel(fieldName)
      };
    });

  return columnName;
});
const showAlertNotification = () => {
  if (globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.alert_message) {
    return (
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode].alert_message.message &&
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode].alert_message.message !==
        ""
    );
  }
  return false;
};
</script>

<template>
  <div v-if="showAlertNotification()" class="mrgn-btm-10">
    <mc-notification
      heading=""
      :body="
        globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.alert_message.message
      "
      :appearance="
        globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.alert_message.category
      "
    >
    </mc-notification>
  </div>
  <div v-if="loader" class="search-card">
    <ShimmerLoaderTableView :msg="labels.equipmentLoader"></ShimmerLoaderTableView>
  </div>
  <div class="devider"></div>

  <div v-if="!errorCode && validIdDetail.length != 0 && !loader">
    <div class="page-header">
      <h3>{{ labels.equipmentResult }} ({{ validIdDetail.length }})</h3>
    </div>

    <div class="search-card">
      <mc-tab-bar id="nav">
        <template v-for="event in validIdDetail" :key="event.containerId">
          <mc-tab slot="tab" fit="large" class="booking-id__tab" :label="event.containerId" icon="container"> </mc-tab>
          <div slot="panel" class="search-card__section">
            <div v-if="event.containerEventDetails.length === 0">
              <div>{{ labels.noResult }}</div>
            </div>
            <div v-else>
              <!-- Header for the container timeline and export pdf or csv button -->
              <div class="item-inline-spaces-between">
                <h3 class="page-header">{{ labels.operator }} - {{ event.shippingLine }}</h3>
                <ContainerPrintView
                  :containers="event.containerEventDetails"
                  fileName="Container-Equipment-History"
                  :headerLabel="containerColumnNames"
                  :isDisabled="event.containerEventDetails.length === 0"
                  :terminalOneStreamCode="locationStore.terminalSelected.oneStreamCode"
                  :analyticsTerminalDetails="locationStore.anayticsTerminalDetails"
                />
              </div>
              <!-- Ends here -->
              <div class="divider"></div>
              <div>
                <mc-table
                  headersticky
                  :data="event.containerEventDetails"
                  :columns="containerColumnNames"
                  :currentpage="currentPage"
                  sortdisabled="true"
                  fit="small"
                  recordsperpage="25"
                  class="appointment-listing-table"
                  height="auto"
                  style="width: 100%"
                >
                  <template v-for="row in event.containerEventDetails" :key="row.id">
                    <!-- Performed on date -->
                    <div :slot="`${row.id}_performedDateTimeLocal`">
                      {{ dateFormatWithoutOffset(row.performedDateTimeLocal) }}
                    </div>
                    <!-- Performed on date -->
                    <div :slot="`${row.id}_eventType`">
                      {{ row.eventType }}
                    </div>
                    <!-- Performed on date -->
                    <div :slot="`${row.id}_positionNotes`">
                      {{ row.positionNotes }}
                    </div>
                  </template>
                </mc-table>
              </div>
            </div>
          </div>
        </template>
      </mc-tab-bar>
    </div>
    <div class="spacer"></div>

    <div class="discliamer_section">{{ labels.vesselDisclaimer }}</div>
  </div>
</template>
